import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Cog, Loader2 } from 'lucide-react';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const validate = () => {
    const newErrors = {};
    
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    } else if (username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      await login(username, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      // Prefer normalized message from the API client (set for timeouts/aborts/network)
      const message = error.normalizedMessage || error.response?.data?.detail || 'Invalid username or password';
      toast.error(message);
      setErrors({ submit: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="login-image"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1701448150058-43d6d199b103?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-10 p-8">
          <Cog className="w-16 h-16 mb-6 opacity-90" />
          <h2 
            className="text-4xl font-bold mb-4 text-center"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Paras Hi-Tech
          </h2>
          <p className="text-lg opacity-80 text-center max-w-md">
            Industrial Heavy Machinery Parts - Admin Management System
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="login-form-container">
        <div className="login-form">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
              <Cog className="w-7 h-7 text-white" />
            </div>
          </div>
          
          <div className="mb-8">
            <h1 
              className="text-2xl font-bold text-slate-900 mb-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Welcome Back
            </h1>
            <p className="text-slate-500">
              Sign in to access the admin panel
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setErrors({ ...errors, username: '' });
                }}
                className={errors.username ? 'border-red-500' : ''}
                data-testid="username-input"
                disabled={isSubmitting}
              />
              {errors.username && (
                <p className="text-sm text-red-500" data-testid="username-error">
                  {errors.username}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors({ ...errors, password: '' });
                  }}
                  className={`pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  data-testid="password-input"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500" data-testid="password-error">
                  {errors.password}
                </p>
              )}
            </div>

            {errors.submit && (
              <div 
                className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
                data-testid="submit-error"
              >
                {errors.submit}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={isSubmitting}
              data-testid="login-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          
        </div>
      </div>
    </div>
  );
};

export default Login;
